/**
 * Версионируемая конфигурация Adaptive Word Engine.
 *
 * Коэффициенты вынесены сюда, а не зашиты по коду (ТЗ FR-EN-04, NFR-Q-04).
 * Версия меняется вместе с формулой: старые результаты не должны молча
 * пересчитываться по новым весам.
 */

/** Веса слагаемых приоритета. Сумма обязана быть равна 1. */
export interface PriorityWeights {
  readonly errorScore: number;
  readonly hesitationScore: number;
  readonly realWorldFrequency: number;
  readonly spacedRepetitionDue: number;
  readonly transferGap: number;
  readonly handBalanceNeed: number;
}

/** Доли материала в упражнении. Сумма обязана быть равна 1. */
export interface MaterialMix {
  /** Слабые слова и сочетания. */
  readonly weak: number;
  /** Материал, срок повторения которого наступил. */
  readonly due: number;
  /** Новый материал. */
  readonly fresh: number;
  /** Освоенное — для поддержания автоматизма. */
  readonly maintenance: number;
  /** Незнакомый контрольный материал (Transfer Guard). */
  readonly transfer: number;
}

/** Интервалы повторения в часах, по возрастанию. */
export type ReviewLadder = readonly number[];

/** Полная конфигурация обучающего алгоритма. */
export interface LearningConfig {
  readonly version: string;
  readonly weights: PriorityWeights;
  readonly baseMix: MaterialMix;
  readonly reviewLadderHours: ReviewLadder;
  /** Верхняя граница показов одной единицы подряд (ТЗ FR-EN-06). */
  readonly maxConsecutiveRepeats: number;
  /** Диапазон учебной точности (ТЗ §7.7). */
  readonly targetAccuracyRange: readonly [number, number];
  /** Число последовательных успехов, после которого единица считается освоенной. */
  readonly successesToMaster: number;
  /** Задержка, начиная с которой нажатие считается неуверенным, мс. */
  readonly hesitationThresholdMs: number;
  /** Задержка, при которой неуверенность максимальна, мс. */
  readonly hesitationCeilingMs: number;
}

export const LEARNING_CONFIG_V1: LearningConfig = Object.freeze({
  version: '1.0.0',
  weights: Object.freeze({
    errorScore: 0.3,
    hesitationScore: 0.22,
    realWorldFrequency: 0.18,
    spacedRepetitionDue: 0.15,
    transferGap: 0.1,
    handBalanceNeed: 0.05,
  }),
  baseMix: Object.freeze({
    weak: 0.45,
    due: 0.25,
    fresh: 0.15,
    maintenance: 0.1,
    transfer: 0.05,
  }),
  // Конец микросессии, позже в этой сессии, следующий день, ~3 дня, ~7 дней.
  reviewLadderHours: Object.freeze([0.05, 0.5, 24, 72, 168]),
  maxConsecutiveRepeats: 3,
  targetAccuracyRange: Object.freeze([0.92, 0.97]) as readonly [number, number],
  successesToMaster: 3,
  hesitationThresholdMs: 350,
  hesitationCeilingMs: 1200,
});

/** Конфигурация по умолчанию. */
export const DEFAULT_LEARNING_CONFIG = LEARNING_CONFIG_V1;
